// UsersTable.js

import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Badge } from '@mui/material';
import { CheckBox, CheckBoxOutlineBlank, Business } from '@mui/icons-material';
import styled from '@emotion/styled';

import './UsersTable.css';
import B2BTable from '../../../b2b-common/B2BTable';
import { LoginContext } from '../../../../App';
import { fetchBasicUserDataByUUID } from '../../../../services/b2b-users-apiService';

const StyledBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
        right: -5,
        top: 10,
        border: `1px solid ${theme.palette.background.paper}`,
        padding: '0 1px',
    },
}));

const UsersTable = ({ usersPagedData, onSelectUser, pagesCount, currentPage, onPageChange, className }) => {

    const { loginData } = useContext(LoginContext);
    const [error, setError] = React.useState(null);
    const [rowLoading, setRowLoading] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);

    const filteredUsers = usersPagedData.content.filter((user) => user.userDTO.userId !== 1);

    useEffect(() => {
        //console.log('UsersTable useEffect selectedUser', selectedUser);
        if (onSelectUser && selectedUser) {
            onSelectUser(selectedUser);
        }
    }, [selectedUser])

    const fetchUserData = async (uuid) => {
        try {
            if (loginData) {
                setRowLoading(uuid);
                const { accessToken } = loginData;
                const data = await fetchBasicUserDataByUUID(uuid, accessToken);
                setSelectedUser(data);
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setRowLoading(null);
        }
    }

    const handleRowSelected = (rowSelected) => {
        fetchUserData(rowSelected.id);
    };

    const headers = [
        { label: 'User Name', align: 'left' },
        { label: 'eMail', align: 'left' },
        { label: 'Organizations', align: 'center' },
        { label: 'Enabled', align: 'center' },
    ];

    const rows = filteredUsers.map((user) => ({
        id: user.userDTO.userUuid,
        cells: [
            { content: `${user.userDTO.firstName} ${user.userDTO.lastName}`, align: 'left' },
            { content: user.userDTO.email, align: 'left' },
            {
                content: (
                    <StyledBadge badgeContent={user.membershipCount} color="secondary" showZero className="badge">
                        <Business color="action" className="icon" />
                    </StyledBadge>
                ),
                align: 'center',
            },
            {
                content: user.userDTO.enabled ? (
                    <CheckBox className="icon checked" />
                ) : (
                    <CheckBoxOutlineBlank className="icon" />
                ),
                align: 'center',
            },
        ],
    }));

    //console.log('UsersTable is being rendered', usersPagedData);

    return <B2BTable
        pagesCount={pagesCount}
        currentPage={currentPage}
        onPageChange={onPageChange}
        headers={headers} rows={rows}
        rowLoading={rowLoading}
        onRowClick={handleRowSelected}
        className={className} />;
};

UsersTable.propTypes = {
    usersPagedData: PropTypes.shape({
        content: PropTypes.arrayOf(PropTypes.shape({
            userDTO: PropTypes.shape({
                userId: PropTypes.number.isRequired,
                userUuid: PropTypes.string.isRequired,
                firstName: PropTypes.string.isRequired,
                lastName: PropTypes.string.isRequired,
                email: PropTypes.string.isRequired,
                enabled: PropTypes.bool.isRequired,
                userOrganizations: PropTypes.array.isRequired,
            }).isRequired,
            membershipCount: PropTypes.number.isRequired,
        })),
    }),
    onSelectUser: PropTypes.func,
    className: PropTypes.string,
};

UsersTable.defaultProps = {
    usersPagedData: { content: [] },
    onSelectUser: null,
    className: '',
};

export default UsersTable;